import * as Sentry from "@sentry/tanstackstart-react";

function reportToPostHog(err: unknown, context?: Sentry.CaptureContext): void {
  const extra =
    context && typeof context === "object" && "extra" in context
      ? (context as { extra?: Record<string, unknown> }).extra
      : undefined;

  if (typeof window !== "undefined") {
    import("posthog-js")
      .then(({ default: posthog }) => {
        posthog.captureException(err, extra);
      })
      .catch(() => {});
  } else {
    import("@/utils/posthog-server")
      .then(({ getPostHogClient }) => {
        const client = getPostHogClient();
        if (!client) return;
        client.captureException(
          err instanceof Error ? err : new Error(String(err)),
        );
      })
      .catch(() => {});
  }
}

export const UNAUTHORIZED = "UNAUTHORIZED";
export const FORBIDDEN = "FORBIDDEN";
export const NOT_FOUND = "NOT_FOUND";
export const BAD_REQUEST = "BAD_REQUEST";

export enum ErrorKind {
  UNAUTHORIZED = "UNAUTHORIZED",
  FORBIDDEN = "FORBIDDEN",
  NOT_FOUND = "NOT_FOUND",
  BAD_REQUEST = "BAD_REQUEST",
  TOO_MANY_REQUESTS = "TOO_MANY_REQUESTS",
  INTERNAL_SERVER_ERROR = "INTERNAL_SERVER_ERROR",
}

export function makeError(code: ErrorKind, message: string = code) {
  throw new Error(
    JSON.stringify({
      code,
      message,
    }),
  );
}

function isErrorCode(error: unknown, code: string): boolean {
  try {
    const parsed = JSON.parse((error as Error).message) as {
      code: string;
      message: string;
    };

    return parsed.code === code;
  } catch (_err) {
    return false;
  }
}

export function isUnauthorized(error: unknown): boolean {
  return isErrorCode(error, ErrorKind.UNAUTHORIZED);
}

export function isForbidden(error: unknown): boolean {
  return isErrorCode(error, ErrorKind.FORBIDDEN);
}

export function isNotFound(error: unknown): boolean {
  return isErrorCode(error, ErrorKind.NOT_FOUND);
}

export function isBadRequest(error: unknown): boolean {
  return isErrorCode(error, ErrorKind.BAD_REQUEST);
}

export function isTooManyRequests(error: unknown): boolean {
  return isErrorCode(error, ErrorKind.TOO_MANY_REQUESTS);
}

export function isInternalServerError(error: unknown): boolean {
  return isErrorCode(error, ErrorKind.INTERNAL_SERVER_ERROR);
}

/** Detects a TRPCClientError with a NOT_FOUND code thrown by tRPC queries. */
export function isTRPCNotFound(error: unknown): boolean {
  return (
    error instanceof Error &&
    error.name === "TRPCClientError" &&
    typeof (error as Record<string, unknown>).data === "object" &&
    (error as { data?: { code?: string } }).data?.code === "NOT_FOUND"
  );
}

/**
 * Capture an error with Sentry and log it to the console.
 * Sentry no-ops when not initialized or disabled (e.g. in dev),
 * so this is safe to call in all environments.
 * Usable as a `.catch()` callback: `.catch(captureError)`
 *
 * Pass optional Sentry context (tags, extras, etc.) as the second argument
 * for structured reporting.
 */
export function captureError(
  err: unknown,
  context?: Sentry.CaptureContext,
): void {
  Sentry.captureException(err, context);
  reportToPostHog(err, context);
  console.error(err);
}

/**
 * Capture a message with Sentry and log it to the console.
 * Use for non-exception events that still need visibility
 * (e.g. warnings, declined refunds).
 */
export function captureMessage(
  message: string,
  context?: Sentry.CaptureContext,
): void {
  Sentry.captureMessage(message, context);
  console.error(message);
}
